var React = require('react');
var Autocomplete = require('@mui/material/Autocomplete').default;
var TextField = require('@mui/material/TextField').default;
var { useSelector, useDispatch } = require('react-redux');
var { useTranslation } = require('react-i18next');
var meteorologicalStationsActions = require('@actions/meteorologicalStationsActions');
var {
  buildStationRows,
  pickableStationTypes,
  pickableStations,
  fieldCenter,
  nearestStation,
  typeOf,
  NEAREST_STATION_MAX_KM,
  formatStationType,
  formatStationLabel,
} = require('./meteorologicalStationsHelpers');
var addIcon = require('images/icons/edit_field/icon_plus.svg');
var locateIcon = require('images/icons/edit_field/navigate_icon.svg');

// The same arrow the Bootstrap .custom-select draws (styles/main.css).
const SELECT_ARROW = (
  <img
    src={require('images/icons/header/down_arrow.svg')}
    style={{ width: '8px', height: '10px' }}
  />
);

// Type and station dropdowns share one width.
const STATION_PICKER_WIDTH = 250;

// Match the Bootstrap .form-control / .custom-select next to it (font, colour,
// 38px height, #ced4da border) instead of MUI's Roboto defaults.
const STATION_AUTOCOMPLETE_SX = {
  width: STATION_PICKER_WIDTH,
  '& .MuiInputBase-root': {
    fontFamily: 'inherit',
    color: '#495057',
    height: '38px',
    paddingLeft: '6px',
  },
  '& .MuiInputBase-input::placeholder': { color: '#495057', opacity: 1 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ced4da' },
  '& .MuiAutocomplete-endAdornment': { right: '12px' },
  '& .MuiAutocomplete-popupIndicator': { padding: 0 },
};

// Same look as the "Add New" buttons elsewhere in Edit Field (SensorsWrapper).
const smallButtonStyle = (disabled) => ({
  fontSize: 12,
  height: '24px',
  borderColor: disabled ? '#D0D3D9' : 'initial',
  background: disabled ? '#D0D3D9' : '#4563C7',
});
const SMALL_BUTTON_CLASS =
  'btn btn-primary ml-2 pr-2 pl-2 py-0 blue-primary align-items-center d-flex justify-content-center text-nowrap';
const BUTTON_ICON_STYLE = { height: '12px', width: '12px' };

const MeteorologicalData = ({
  field,
  onStationAdd,
  onStationRemove,
  onStationMove,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const availableStations = useSelector(
    (state) => state.meteorological_stations_data?.stations,
  );

  const [selectedStationType, setSelectedStationType] = React.useState('');
  const [selectedStationId, setSelectedStationId] = React.useState('');
  const [nearestMessage, setNearestMessage] = React.useState('');
  // Drag-to-reorder: index of the row being dragged and of the row under it.
  const [dragIndex, setDragIndex] = React.useState(null);
  const [dropIndex, setDropIndex] = React.useState(null);
  const meteorologicalSources = field?.geo_data?.meteorological_sources || [];
  const currentStationIds = field?.geo_data?.meteorological_stations || [];

  React.useEffect(() => {
    dispatch(meteorologicalStationsActions.load_meteorological_stations());
  }, [dispatch]);

  // Pick a station type first, then an active station of that type. Inactive
  // stations and ones already on the field are left out.
  const stationTypes = pickableStationTypes(
    availableStations,
    currentStationIds,
  );
  const stationsOfType = selectedStationType
    ? pickableStations(
        availableStations,
        currentStationIds,
        selectedStationType,
      )
    : [];

  const selectedStation =
    stationsOfType.find(
      (station) => String(station.id) === selectedStationId,
    ) || null;

  // Rows come from the IDs being edited, not from the saved source names, so a
  // removed station disappears right away and duplicates stay separate rows.
  const stationRows = buildStationRows(
    currentStationIds,
    availableStations,
    meteorologicalSources,
  );

  const fieldAreas = field?.geo_data?.areas;

  const canReorder = Boolean(onStationMove) && stationRows.length > 1;
  const endDrag = () => {
    setDragIndex(null);
    setDropIndex(null);
  };
  // The dragged row is dimmed; a blue line marks where it will land: above the
  // target when moving up, below it when moving down.
  const stationRowStyle = (index) => {
    const style = { padding: '2px 0', borderTop: '2px solid transparent' };
    style.borderBottom = '2px solid transparent';
    if (dragIndex === index) style.opacity = 0.4;
    if (dragIndex !== null && dropIndex === index && dropIndex !== dragIndex) {
      const edge = dropIndex < dragIndex ? 'borderTop' : 'borderBottom';
      style[edge] = '2px solid #4563C7';
    }
    if (canReorder) style.cursor = 'grab';
    return style;
  };

  // Pre-select the closest active station to the field polygon; the user still
  // presses "add" to put it on the field.
  const handleFindNearest = () => {
    const nearest = nearestStation(
      availableStations,
      currentStationIds,
      fieldAreas,
    );
    if (!nearest) {
      setNearestMessage(t('no_station_nearby', { km: NEAREST_STATION_MAX_KM }));
      return;
    }
    setSelectedStationType(typeOf(nearest.station));
    setSelectedStationId(String(nearest.station.id));
    setNearestMessage(
      t('nearest_station_found', {
        station: nearest.station.label,
        km: nearest.km.toFixed(1),
      }),
    );
  };

  const handleStationTypeChange = (stationType) => {
    setSelectedStationType(stationType);
    setSelectedStationId('');
    setNearestMessage('');
  };

  const handleAddStation = () => {
    if (selectedStationId && onStationAdd) {
      onStationAdd(selectedStationId);
      setSelectedStationId('');
      setNearestMessage('');
    }
  };

  return (
    <div className='form-row justify-content-start'>
      <div className='col-12'>
        <div className='form-group ml-2 mr-2 mt-3'>
          <label
            className='font-weight-bold text-size-20'
            htmlFor='meteorologicalData'
          >
            {t('meteorological_data')}
          </label>
          <div className='mt-2'>
            {stationRows.length === 0 ? (
              <div className='text-grey71 text-size-14'>
                {t('no_custom_station_added')}
              </div>
            ) : (
              stationRows.map((row) => (
                <div
                  key={`${row.id}-${row.index}`}
                  className='d-flex align-items-center text-size-14 font-weight-normal text-grey45'
                  style={stationRowStyle(row.index)}
                  draggable={canReorder}
                  onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = 'move';
                    // Firefox only starts a drag when some data is set.
                    event.dataTransfer.setData('text/plain', String(row.index));
                    setDragIndex(row.index);
                  }}
                  onDragOver={(event) => {
                    if (dragIndex === null) return;
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'move';
                    if (dropIndex !== row.index) setDropIndex(row.index);
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    if (dragIndex !== null) onStationMove(dragIndex, row.index);
                    endDrag();
                  }}
                  onDragEnd={endDrag}
                >
                  {canReorder && (
                    <span
                      className='mr-2 text-grey71'
                      style={{ cursor: 'grab', userSelect: 'none' }}
                      title={t('drag_to_reorder_station')}
                      aria-hidden='true'
                    >
                      ⠿
                    </span>
                  )}
                  {stationRows.length > 1 && (
                    <span className='mr-1'>{row.index + 1}.</span>
                  )}
                  <span>{formatStationLabel(row.label)}</span>
                  {onStationRemove && (
                    <button
                      type='button'
                      className='btn p-0 ml-2 d-flex align-items-center'
                      style={{ outline: 'none', boxShadow: 'none' }}
                      aria-label={t('remove_station')}
                      title={t('remove_station')}
                      onClick={() => onStationRemove(row.index)}
                    >
                      <img
                        src={require('images/icons/edit_field/close_icon.svg')}
                        style={{ width: '10px', height: '10px' }}
                      />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          {canReorder && (
            <div className='mt-1 text-grey71 text-size-12'>
              {t('station_priority_hint')}
            </div>
          )}

          {onStationAdd && stationTypes.length > 0 && (
            <div className='mt-3 d-flex align-items-center'>
              <select
                className='form-control custom-select'
                style={{ width: STATION_PICKER_WIDTH, background: 'none' }}
                value={selectedStationType}
                onChange={(e) => handleStationTypeChange(e.target.value)}
              >
                <option value=''>{t('select_station_type')}</option>
                {stationTypes.map((stationType) => (
                  <option key={stationType} value={stationType}>
                    {formatStationType(stationType)}
                  </option>
                ))}
              </select>
              <Autocomplete
                className='ml-2'
                size='small'
                sx={STATION_AUTOCOMPLETE_SX}
                options={stationsOfType}
                getOptionLabel={(station) => station.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={selectedStation}
                onChange={(_, station) => {
                  setSelectedStationId(station ? String(station.id) : '');
                  setNearestMessage('');
                }}
                popupIcon={SELECT_ARROW}
                disabled={!selectedStationType}
                noOptionsText={t('no_stations_found')}
                // The popup is portalled to <body>; .modal is z-index 10000000
                // (styles/main.css), so lift it above the Edit Field modal.
                slotProps={{
                  popper: { style: { zIndex: 10000001 } },
                  paper: { sx: { fontFamily: 'inherit', color: '#495057' } },
                }}
                renderOption={(props, station) => (
                  <li {...props} key={station.id}>
                    {station.label}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} placeholder={t('select_station')} />
                )}
              />
              <button
                type='button'
                className={SMALL_BUTTON_CLASS}
                style={smallButtonStyle(!selectedStationId)}
                onClick={handleAddStation}
                disabled={!selectedStationId}
              >
                <img
                  src={addIcon}
                  style={BUTTON_ICON_STYLE}
                  className='mx-1'
                  alt=''
                />
                {t('add_station')}
              </button>
              {fieldCenter(fieldAreas) && (
                <button
                  type='button'
                  className={SMALL_BUTTON_CLASS}
                  style={smallButtonStyle(false)}
                  onClick={handleFindNearest}
                >
                  <img
                    src={locateIcon}
                    // The icon is drawn dark grey; turn it white on the blue.
                    style={{
                      ...BUTTON_ICON_STYLE,
                      filter: 'brightness(0) invert(1)',
                    }}
                    className='mx-1'
                    alt=''
                  />
                  {t('find_nearest_station')}
                </button>
              )}
            </div>
          )}
          {nearestMessage && (
            <div className='mt-1 text-grey71 text-size-14'>
              {nearestMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

module.exports = MeteorologicalData;
